import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Payment } from '@Types/cart/Payment';
import { Order } from '@Types/cart/Order';
import { Trustbadge } from '@Types/trustedshops/Trustbadge';
import { Trustcard } from '@Types/trustedshops/Trustcard';
import { Trans, useTranslation } from 'react-i18next';
import { MediaItemWithMods, useAccount, useCart, useTrustedShops } from 'frontastic';
import Image from 'frontastic/lib/image';
import { TagManager } from '../../../frontastic/lib/tag-manager';
import { ReferenceLink } from '../../../helpers/reference';
import Spinner from '../../commercetools-ui/spinner';
import TrustbadgeComponent from '../../trusted-shops/trustbadge';
import TrustcardComponent from '../../trusted-shops/trustcard';
import { PaymentHandler } from '../checkout/utils/PaymentHandler';
import OrderError, { ErrorDetails } from '../order-error';
import PendingPaymentInfo from './pending-payment-info';
import { Account } from '@Types/account';
import ButtonElement from '../forms/form-elements/ButtonElement';
import FormFieldInput from '../forms/form-field-input';
import { isValidEmail, isValidRegistration } from '../checkout/validation';
import { useFormat } from '../../../helpers/hooks/useFormat';
import { FormProvider, useForm } from 'react-hook-form';
import FormFieldButton from '../forms/form-field-button';
import RegisterForm from './register-form';
import NextLink from 'next/link';
import guest from '../../commercetools-ui/checkout/checkout-form/guest';
import Button from '../content/button';

export type ThankYouProps = {
  title?: string;
  text?: string;
  image?: MediaItemWithMods;
  errorDetails?: ErrorDetails[];
  showTrustcard?: boolean;
};

const ThankYou = ({ title, text, image, showTrustcard, errorDetails }: ThankYouProps) => {
  const { checkout, data, setCustomerToOrder } = useCart();
  const { loggedIn, getAccountByEmail, register } = useAccount();
  const router = useRouter();
  const { t } = useTranslation(['checkout']);
  const { getTrustbadge } = useTrustedShops();
  const { formatMessage: formatSuccessMessage } = useFormat({ name: 'success' });
  const { formatMessage: formatAccountMessage } = useFormat({ name: 'account' });

  const [error, setError] = useState<Error>();
  const [maintext, setMaintext] = useState<string>(text);
  const [trustcard, setTrustcard] = useState<Trustcard | null>();
  const [trustbadge, setTrustbadge] = useState<Trustbadge | null>();
  const [processed, setProcessed] = useState<boolean>(false);
  const [payment, setPayment] = useState<Payment | undefined>(undefined);
  const [order, setOrder] = useState<Order>(null);
  const [customer, setCustomer] = useState<Account>(null);
  const [isGuest, setGuest] = useState<boolean>();
  const [registerData, setRegisterData] = useState({ email: '', password: '', confirmPassword: '' });
  const [success, setSuccess] = useState<boolean>(false);

  const handleSettledPayment = async () => {
    const state = data?.orderState;
    if (state !== undefined && state.toLowerCase() !== 'open') {
      return;
    }

    const tagManager = new TagManager().purchaseEvent(data);
    await checkout()
      .then((response) => {
        tagManager.executePush();

        if (showTrustcard) {
          setTrustcard(response.trustcard ?? null);
        }

        setOrder(response.order);
        setRegisterData({ ...registerData, email: response.order.email });
      })
      .catch((checkoutError) => {
        setError(checkoutError);
      })
      .finally(() => {
        setProcessed(true);
      });
  };

  const addToCustomer = async () => {
    setProcessed(false);
    try {
      const response = await setCustomerToOrder(order, customer);
      if (response.order) {
        if (response.sendConfirmation) {
          setSuccess(true);

          setMaintext(
            formatSuccessMessage({
              id: 'account.linkedToOrderWithVerification',
              defaultMessage:
                'Your order has been successfully linked to your account. You can find the order in your account. A confirmation email has been sent to {email}.',
              values: { email: response.order.email },
            }),
          );
        }

        setSuccess(true);
        setMaintext(
          formatSuccessMessage({
            id: 'account.linkedToOrder',
            defaultMessage:
              'Your order has been successfully linked to your account. You can find the order in your account.',
          }),
        );
      }
      setProcessed(true);
    } catch (error) {
      setProcessed(true);
      setError(error);
    }
  };

  useEffect(() => {
    if (data === undefined || processed) {
      return;
    }

    const pendingPayment = PaymentHandler.getPendingPayment(data);
    const settledPayment = PaymentHandler.getSettledPayment(data);

    if (!settledPayment && !pendingPayment) {
      (async () => {
        await router.push('/checkout');
      })();
    } else if (settledPayment !== undefined) {
      setPayment(settledPayment);
      handleSettledPayment();
    } else {
      setPayment(pendingPayment);
      setProcessed(true);
    }
  }, [data, router]);

  useEffect(() => {
    if (trustcard) {
      trustcard.id = 'trustcard';
    }
  }, [trustcard]);

  useEffect(() => {
    if (trustbadge === undefined) {
      getTrustbadge().then((trustbadge) => setTrustbadge(trustbadge));
    }
  }, [trustbadge, getTrustbadge]);

  useEffect(() => {
    const checkOrder = async () => {
      if (order && !loggedIn) {
        const account = await getAccountByEmail(order.email);

        if (account) {
          setCustomer(account);
        } else {
          setGuest(true);
        }
      }
    };

    checkOrder();
  }, [order]);

  if (error) {
    return <OrderError image={image} error={error} errorDetails={errorDetails} />;
  }

  if (payment !== undefined && PaymentHandler.isPaymentPending(payment)) {
    return <PendingPaymentInfo payment={payment} />;
  }

  const components = [
    <ReferenceLink
      key={0}
      className="cursor-pointer font-medium text-secondary-600 underline hover:text-secondary-600/90"
      target={{
        type: 'link',
        link: payment !== undefined ? PaymentHandler.getMollieOrderUrl(payment) : null,
        openInNewWindow: false,
      }}
    />,
  ];

  return (
    <main className="flex flex-col">
      {!processed ? (
        <div className="flex w-full items-stretch justify-center px-12 py-10">
          <Spinner />
        </div>
      ) : (
        <section className="grid lg:grid-cols-5">
          <div className="order-1 lg:col-span-3">
            <div className="mx-auto flex-auto px-4 py-5 sm:px-6 lg:px-8 lg:pt-32">
              {title && <p className="mt-7 text-4xl font-semibold text-neutral-950 sm:text-5xl">{title}</p>}
              {maintext && <p className="mt-3 text-base text-neutral-950">{maintext}</p>}
              {success && (
                <>
                  <div className="mt-4">
                    <NextLink
                      href="/account"
                      className="inline-flex min-w-[250px] items-center justify-center rounded-4xl border-2 border-transparent bg-neutral-950 px-8 py-3 text-md font-semibold text-white transition duration-150 ease-out hover:bg-neutral-900 focus:bg-neutral-900"
                    >
                      {formatAccountMessage({
                        id: 'thankYouGoToAccount',
                        defaultMessage: 'To my customer account',
                      })}
                    </NextLink>
                  </div>
                </>
              )}

              {payment !== undefined && !PaymentHandler.isMollieInteractionRequired(payment) && (
                <p className="mt-4 text-base text-neutral-950">
                  <Trans
                    i18nKey="nonInteractivePaymentInfo"
                    t={t}
                    components={components}
                    values={{ paymentMethod: payment.paymentMethod }}
                  />
                </p>
              )}
            </div>
            {customer && !success && (
              <div className="mb-4 flex w-full flex-wrap bg-neutral-50 p-4 lg:w-[95%] lg:p-6">
                <div className="mb-4 basis-full">
                  <div className="text-md font-semibold text-neutral-950">
                    {formatAccountMessage({
                      id: 'thankYouLinkHeadline',
                      defaultMessage: 'Link your order to your account',
                    })}
                  </div>
                  {customer && (
                    <span className="mt-3 text-base text-gray-900 dark:text-light-100">{customer.email}</span>
                  )}
                </div>
                <Button
                  className="pl-5 pr-5 lg:pl-8 lg:pr-8"
                  style="primary"
                  size="large"
                  onClick={addToCustomer}
                  fullWidth={false}
                >
                  {formatAccountMessage({
                    id: 'linkToOrder',
                    defaultMessage: 'Link order to account',
                  })}
                </Button>
              </div>
            )}
            {isGuest && order && !success && (
              <RegisterForm order={order} setMaintext={setMaintext} setSucess={setSuccess} />
            )}
          </div>
          <div className="order-3 flex items-center lg:order-2 lg:col-span-2 lg:w-[486px]">
            {(trustcard || image) && (
              <>
                {trustcard ? (
                  <>
                    <div id="trustbadgeCustomCheckoutContainer"></div>
                    <TrustcardComponent trustcard={trustcard} />
                    {trustbadge && <TrustbadgeComponent trustbadge={trustbadge} />}
                  </>
                ) : (
                  <Image media={image} layout="responsive" objectFit="contain" alt="Thank you" />
                )}
              </>
            )}
          </div>
        </section>
      )}
    </main>
  );
};

export default ThankYou;
